import { Box, Button, Stack } from '@mui/material';
import axios, { AxiosError } from 'axios';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useToast } from '@/components/ToastProvider';
import { subscriptionStatus } from '@/const/account';
import { authSelector } from '@/services/authSlice';
import { useAppSelector } from '@/store/hooks';

import { TrialDetail } from './TrialDetail';
const API_ENDPOINT_URL = process.env.REACT_APP_API_ENDPOINT ?? '';

// API は url が null の 200 を返すことがある（支払える請求が見つからない）。
// Stripe Search は反映が最大1時間遅れうるので「未払いはありません」とは断定しない。
// Stripe 障害時は 503。どちらも以前は素通りして window.location.href = undefined に飛んでいた。
const PAYMENT_PAGE_ERROR = 'お支払いページを開けませんでした。時間をおいて再度お試しください。';
const PAYMENT_PAGE_TIMEOUT =
  'お支払いページの取得に時間がかかっています。時間をおいて再度お試しください。';
const PAYMENT_PAGE_NETWORK = '通信に失敗しました。ネットワーク接続を確認して再度お試しください。';

// axios の既定は無制限。API 側は Stripe Search と請求書取得で通常は数秒なので、
// これを超えたら待たせずに戻す（GET で副作用は無いので途中で切って良い）。
const PAYMENT_REQUEST_TIMEOUT_MS = 15000;

type PaymentFailure = {
  kind: 'timeout' | 'network' | 'http' | 'unknown';
  // 利用者に見せる文言
  message: string;
  // 記録用。axios のメッセージに秘匿情報は含まれない
  detail: string;
  status?: number;
  code?: string;
};

// catch で全部同じ扱いにすると DNS/TLS/CORS/503 の区別がつかず観測できないので分類する。
// このリポジトリにエラー収集基盤は無く、記録先は console.error が上限。
const describePaymentFailure = (err: unknown): PaymentFailure => {
  const detail = err instanceof Error ? err.message : String(err);
  if (axios.isAxiosError(err)) {
    // ブラウザ側の中断（ページ離脱など）も ECONNABORTED で届く。detail の文言で見分けられる
    if (err.code === AxiosError.ECONNABORTED || err.code === AxiosError.ETIMEDOUT) {
      return { kind: 'timeout', code: err.code, detail, message: PAYMENT_PAGE_TIMEOUT };
    }
    if (err.response) {
      return {
        kind: 'http',
        status: err.response.status,
        code: err.code,
        detail,
        message: PAYMENT_PAGE_ERROR,
      };
    }
    return { kind: 'network', code: err.code, detail, message: PAYMENT_PAGE_NETWORK };
  }
  return { kind: 'unknown', detail, message: PAYMENT_PAGE_ERROR };
};

// ボタンを押したときの表示を切り替える関数
const TrialStatus = () => {
  const { account } = useAppSelector(authSelector);
  const { toast } = useToast();
  const [showTrialDetail, setShowTrialDetail] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const paymentRequestRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // 支払いページへ飛んだ後にブラウザの「戻る」で bfcache から復元されると
    // isRedirecting=true のままボタンが押せなくなるので戻す
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setIsRedirecting(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      // Layout は /result 等ではこのコンポーネントを外す。応答前に別ページへ移ったのに
      // 遅れて成功すると、そのページから支払いページへ引き剥がしてしまうので中止する
      paymentRequestRef.current?.abort();
    };
  }, []);

  const openTrialDetails = useCallback(() => {
    setShowTrialDetail(true);
  }, []);

  const closeTrialDetails = useCallback(() => {
    setShowTrialDetail(false);
  }, []);

  const startRegistration = useCallback(() => {
    const queryParameter = `?prefilled_email=${account.email}&client_reference_id=${account.id}`;
    const url =
      account.course_type === 1
        ? process.env.REACT_APP_STRIPE_PAYMENT_LINK_URL
        : process.env.REACT_APP_STRIPE_PAYMENT_LINK_URL_STUDENT;
    window.open(url + queryParameter, '_blank');
  }, []);

  const trialEndDate = account?.trial_end_date
    ? dayjs(account.trial_end_date).format('YYYY/MM/DD')
    : '';
  const trialStatus = account?.subscription_status ?? '';

  const confirmPaymentMethod = async () => {
    if (isRedirecting) return;
    setIsRedirecting(true);
    // bfcache 復元でボタンが戻った後に押し直すと前の要求が残りうる。競合させずに捨てる
    paymentRequestRef.current?.abort();
    const controller = new AbortController();
    paymentRequestRef.current = controller;

    try {
      const response = await axios.get(`${API_ENDPOINT_URL}/subscriptions/payment`, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Authorization: `Bearer ${localStorage.getItem('SaaSusIdToken')}`,
        },
        withCredentials: true,
        timeout: PAYMENT_REQUEST_TIMEOUT_MS,
        signal: controller.signal,
      });
      // 応答が返った直後にアンマウントされた場合。もう画面が無いので何もしない
      if (controller.signal.aborted) return;

      const redirectURL = response.data?.url;
      if (typeof redirectURL !== 'string' || redirectURL === '') {
        console.error('[payment] 支払いページの URL が返らなかった', {
          kind: 'empty_url',
          status: response.status,
        });
        toast({ message: PAYMENT_PAGE_ERROR, type: 'error' });
        setIsRedirecting(false);
        return;
      }

      // 成功時はページを離れるので isRedirecting は戻さない
      window.location.href = redirectURL;
    } catch (err) {
      // アンマウントによる中止。エラーではないし、表示先ももう無い
      if (controller.signal.aborted) return;

      const failure = describePaymentFailure(err);
      console.error('[payment] 支払いページの取得に失敗', {
        kind: failure.kind,
        status: failure.status,
        code: failure.code,
        message: failure.detail,
      });
      toast({ message: failure.message, type: 'error' });
      setIsRedirecting(false);
    } finally {
      if (paymentRequestRef.current === controller) paymentRequestRef.current = null;
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mb: 2, mt: 4, mx: 'auto', px: [2, 2, 0] }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={[4, 6]} sx={{ fontSize: 14 }}>
        {/* トライアル */}
        <Stack
          sx={{
            borderRadius: '16px',
            boxShadow: 2,
            bgcolor: '#f7fafa',
            flex: 1,
            position: 'relative',
          }}
        >
          {/* トライアルタイトル */}
          {/* トライアル期間を過ぎたら別の内容を表示 */}
          {trialStatus === subscriptionStatus.TRIAL_EXPIRED ? (
            <>
              <Box
                sx={{
                  bgcolor: '#ef6c0029',
                  borderTopRightRadius: '16px',
                  borderTopLeftRadius: '16px',
                  fontSize: 18,
                  fontWeight: 'bold',
                  py: 3,
                  textAlign: 'center',
                }}
              >
                トライアルは終了しました
              </Box>
              <Box sx={{ fontSize: 14, textAlign: { xs: 'left', md: 'center' }, px: 2, py: 4 }}>
                <Box>トライアルのご利用ありがとうございました。</Box>
                <Box>引き続きご利用される場合は、本入会の手続きにお進みください。</Box>
              </Box>
              <Button
                variant="contained"
                sx={{
                  mx: 2,
                  mb: 2,
                  height: 41,
                  borderRadius: 1,
                  bgcolor: 'primary.main',
                  color: 'white',
                  boxShadow: 0,
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                }}
                onClick={startRegistration}
              >
                本入会に進む
              </Button>
            </>
          ) : trialStatus === subscriptionStatus.IN_TRIAL ? (
            <>
              <Box
                sx={{
                  bgcolor: '#00a76b29',
                  borderTopRightRadius: '16px',
                  borderTopLeftRadius: '16px',
                  fontSize: 18,
                  fontWeight: 'bold',
                  py: [2, 3],
                  textAlign: 'center',
                }}
              >
                現在トライアル中
              </Box>
              <Stack spacing={[2, 4]} sx={{ p: [2, 4] }}>
                <Box sx={{ fontSize: 18, fontWeight: 'bold', textAlign: 'center' }}>
                  トライアル期間: {trialEndDate}まで
                </Box>
                <Button
                  variant="contained"
                  sx={{
                    mx: 2,
                    mb: 2,
                    height: 41,
                    borderRadius: 1,
                    bgcolor: 'primary.main',
                    color: 'white',
                    boxShadow: 0,
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  }}
                  onClick={startRegistration}
                >
                  本入会に進む
                </Button>
                {showTrialDetail ? (
                  // トライアル詳細説明
                  <TrialDetail onClose={closeTrialDetails} />
                ) : (
                  <Button
                    variant="contained"
                    sx={{
                      mx: 2,
                      mb: 2,
                      height: 41,
                      borderRadius: 1,
                      bgcolor: '#f7fafa',
                      color: '#1976d2',
                      border: '1px solid #1976d2',
                      boxShadow: 0,
                      '&:hover': {
                        bgcolor: '#f7fafa',
                        color: '#1976d2',
                        border: '1px solid #1976d2',
                      },
                    }}
                    onClick={openTrialDetails}
                  >
                    トライアルの説明を見る
                  </Button>
                )}
              </Stack>
            </>
          ) : trialStatus === subscriptionStatus.UNPAID ? (
            <>
              <Box
                sx={{
                  bgcolor: '#d81b6052',
                  borderTopRightRadius: '16px',
                  borderTopLeftRadius: '16px',
                  fontSize: 18,
                  fontWeight: 'bold',
                  py: 3,
                  textAlign: 'center',
                }}
              >
                お支払いの確認ができておりません
              </Box>
              <Box sx={{ fontSize: 14, textAlign: { xs: 'left', md: 'center' }, px: 2, py: 4 }}>
                <Box>
                  お客様が設定した支払い方法での決済ができませんでした。支払い方法を確認してください。
                </Box>
                <Box>月末までにお支払いの確認ができなかった場合、一部機能に制限がかかります。</Box>
              </Box>
              <Button
                variant="contained"
                sx={{
                  mx: 2,
                  mb: 2,
                  height: 41,
                  borderRadius: 1,
                  bgcolor: 'primary.main',
                  color: 'white',
                  boxShadow: 0,
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                }}
                onClick={confirmPaymentMethod}
                disabled={isRedirecting}
              >
                支払いフォームへ移動する
              </Button>
            </>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
};

export default TrialStatus;
